import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { isThemeFlagOn } from "../../../pages/gameSetupStorage";
import RulesFeatureTour, {
  markRulesTourSeen,
  shouldAutoStartRulesTour,
} from "./RulesFeatureTour";

const MAX_RULES = 6;
const MIN_RULES = 1;
const MAX_RULE_CHARS = 200;
const MIN_POINTS = 1;
const MAX_POINTS = 100;
const MAX_TITLE_CHARS = 250;
const MAX_THANKYOU_CHARS = 300;
const MAX_TAGLINE_CHARS = 60;
const DEFAULT_LEADERBOARD_TAGLINE = "Great people build great workplaces";
/** The thank-you message field is hidden for now; the saved value is still loaded and
 *  re-saved unchanged, so turning this back on restores it. */
const SHOW_THANKYOU_TEXT = false;
const DEFAULT_HINT_PENALTY = 2;
const MAX_HINT_PENALTY = 50;
const DEFAULT_HINT_TIME_PENALTY = 10;
const MAX_HINT_TIME_PENALTY = 999;

const HINT_PENALTY_TYPES = [
  { value: "none", label: "None" },
  { value: "points", label: "Points" },
  { value: "time", label: "Time" },
  { value: "both", label: "Both" },
];

const HINT_PENALTY_HELP = {
  none: "Reveals a letter with no penalty",
  points: "Reveals a letter and deducts points",
  time: "Reveals a letter and adds seconds to Time Taken",
  both: "Reveals a letter, deducts points and adds seconds",
};

/** Themes saved before hint_penalty_type existed only had a points penalty. */
function toHintPenaltyType(raw) {
  const v = String(raw ?? "").trim().toLowerCase();
  return HINT_PENALTY_TYPES.some((t) => t.value === v) ? v : "points";
}

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

function toColorInputValue(val) {
  if (!val || typeof val !== "string") return "#000000";
  const v = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_COLORS[v.toLowerCase()];
  if (named) return named;
  return "#000000";
}

const normalizeCountInput = (raw) => String(raw).replace(/\D/g, "");

/** Digits only, capped at `max` so the field can't hold a value over the limit. */
const clampCountInput = (raw, max) => {
  const digits = normalizeCountInput(raw);
  return digits && parseInt(digits, 10) > max ? String(max) : digits;
};

function parseRulesList(raw) {
  if (Array.isArray(raw)) return raw.map((r) => String(r ?? ""));
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((r) => String(r ?? ""));
    } catch {
      /* not JSON */
    }
  }
  return [];
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([""]);
  const [titleText, setTitleText] = useState("");
  const [thankYouText, setThankYouText] = useState("");
  const [leaderboardTagline, setLeaderboardTagline] = useState(DEFAULT_LEADERBOARD_TAGLINE);
  const [correctPoints, setCorrectPoints] = useState(String(MIN_POINTS));
  const [textColor, setTextColor] = useState("#000000");
  const [buttonColor, setButtonColor] = useState("#e9695e");
  const [buttonTextColor, setButtonTextColor] = useState("#ffffff");
  const [gridShuffle, setGridShuffle] = useState(false);
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);
  const [hintEnabled, setHintEnabled] = useState(true);
  const [hintPenaltyType, setHintPenaltyType] = useState("points");
  const [hintPenalty, setHintPenalty] = useState(String(DEFAULT_HINT_PENALTY));
  const [hintTimePenalty, setHintTimePenalty] = useState(String(DEFAULT_HINT_TIME_PENALTY));
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (status === "loading" && !data) return undefined;
    if (!shouldAutoStartRulesTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 600);
    return () => window.clearTimeout(t);
  }, [status, data]);

  const closeTour = () => {
    markRulesTourSeen();
    setTourOpen(false);
  };

  useEffect(() => {
    if (!data) return;
    const loadedRules = parseRulesList(data.rules);
    setRules(loadedRules.length ? loadedRules : [""]);
    setTitleText(String(data.landing_page_title ?? "").slice(0, MAX_TITLE_CHARS));
    setThankYouText(String(data.custom_text_thank_you_page ?? "").slice(0, MAX_THANKYOU_CHARS));
    // Themes saved before this field existed show the text the leaderboard always used.
    setLeaderboardTagline(
      (String(data.leaderboard_tagline ?? "").trim() || DEFAULT_LEADERBOARD_TAGLINE).slice(
        0,
        MAX_TAGLINE_CHARS
      )
    );
    setCorrectPoints(String(parseInt(data.correctPoints ?? data.points, 10) || MIN_POINTS));
    setTextColor(toColorInputValue(data.landing_page_title_color));
    setButtonColor(toColorInputValue(data.button_color || "#e9695e"));
    setButtonTextColor(toColorInputValue(data.button_Textcolor || "#ffffff"));
    // Themes saved before this setting existed keep the single shared grid.
    setGridShuffle(isThemeFlagOn(data.grid_shuffle, false));
    setLeaderboardEnabled(isThemeFlagOn(data.leaderboard, true));
    // Themes saved before this setting existed keep the hint on at the old fixed -2.
    setHintEnabled(isThemeFlagOn(data.hint_enabled, true));
    const penalty = parseInt(data.hint_penalty, 10);
    setHintPenalty(String(Number.isFinite(penalty) && penalty >= 0 ? penalty : DEFAULT_HINT_PENALTY));
    setHintPenaltyType(toHintPenaltyType(data.hint_penalty_type));
    const timePenalty = parseInt(data.hint_time_penalty, 10);
    setHintTimePenalty(
      String(Number.isFinite(timePenalty) && timePenalty >= 0 ? timePenalty : DEFAULT_HINT_TIME_PENALTY)
    );
  }, [data]);

  const updateRule = (index, value) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? value : rule)));
  };

  const addRule = () => {
    if (rules.length >= MAX_RULES) {
      Swal.fire("Error", `Maximum ${MAX_RULES} rules allowed`, "error");
      return;
    }
    setRules((prev) => [...prev, ""]);
  };

  const removeRule = (index) => {
    if (rules.length <= MIN_RULES) {
      Swal.fire("Error", "At least 1 rule is required", "error");
      return;
    }
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const trimmed = rules.map((r) => String(r ?? "").trim());
    const filled = trimmed.filter(Boolean);
    const errors = [];

    if (!filled.length) {
      errors.push("At least 1 rule is required");
    }
    const longRules = trimmed
      .map((text, i) => (text.length > MAX_RULE_CHARS ? i + 1 : null))
      .filter(Boolean);
    if (longRules.length) {
      errors.push(`Rules ${longRules.join(", ")} exceed ${MAX_RULE_CHARS} characters`);
    }
    if (rules.length > MAX_RULES) {
      errors.push(`Maximum ${MAX_RULES} rules allowed`);
    }
    if (titleText.trim().length > MAX_TITLE_CHARS) {
      errors.push(`Custom title text exceeds ${MAX_TITLE_CHARS} characters`);
    }
    if (thankYouText.trim().length > MAX_THANKYOU_CHARS) {
      errors.push(`Thank you page text exceeds ${MAX_THANKYOU_CHARS} characters`);
    }
    if (!leaderboardTagline.trim()) {
      errors.push("Leaderboard tagline is required");
    } else if (leaderboardTagline.trim().length > MAX_TAGLINE_CHARS) {
      errors.push(`Leaderboard tagline exceeds ${MAX_TAGLINE_CHARS} characters`);
    }
    if (parseInt(correctPoints, 10) < MIN_POINTS || correctPoints === "") {
      errors.push("You must set at least 1 correct answer point.");
    } else if (parseInt(correctPoints, 10) > MAX_POINTS) {
      errors.push(`Total marks per right answer can be at most ${MAX_POINTS}.`);
    }
    const usesPointPenalty = hintPenaltyType === "points" || hintPenaltyType === "both";
    const usesTimePenalty = hintPenaltyType === "time" || hintPenaltyType === "both";
    if (hintEnabled && usesPointPenalty) {
      const n = parseInt(hintPenalty, 10);
      if (hintPenalty === "" || !(n >= 1 && n <= MAX_HINT_PENALTY)) {
        errors.push(`Points deducted per hint must be between 1 and ${MAX_HINT_PENALTY}.`);
      }
    }
    if (hintEnabled && usesTimePenalty) {
      const n = parseInt(hintTimePenalty, 10);
      if (hintTimePenalty === "" || !(n >= 1 && n <= MAX_HINT_TIME_PENALTY)) {
        errors.push(`Penalty seconds per hint must be between 1 and ${MAX_HINT_TIME_PENALTY}.`);
      }
    }

    if (errors.length) return { ok: false, msg: errors.join("\n") };

    const payload = {
      landing_page_title: titleText.trim(),
      landing_page_title_color: textColor,
      button_color: buttonColor,
      button_Textcolor: buttonTextColor,
      points: parseInt(correctPoints, 10),
      grid_shuffle: gridShuffle,
      leaderboard: leaderboardEnabled,
      hint_enabled: hintEnabled,
      hint_penalty_type: hintPenaltyType,
      // Both amounts are kept whatever the type, so switching back restores the last values.
      hint_penalty: parseInt(hintPenalty, 10) || 0,
      hint_time_penalty: parseInt(hintTimePenalty, 10) || 0,
      custom_text_thank_you_page: thankYouText.trim(),
      leaderboard_tagline: leaderboardTagline.trim(),
      rules: filled,
    };

    return { ok: true, payload, filled };
  };

  const handleSave = async ({ goNext = false } = {}) => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Admin session or theme is missing.", "error");
      return;
    }

    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success", "Data Updated", "success");
      if (goNext) navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="rules-shell">
      <div className="back-button-holder rules-top-bar admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <div className="rules-page-header">
        <h1 className="rules-page-header-title">Rules &amp; Game Settings</h1>
        <p className="rules-page-header-desc">
          Configure what players read and the colors used throughout this theme.
        </p>
      </div>

      <div className="rules-main-grid">
        <section className="rules-card" data-tour="tour-theme-settings">
          <header className="rules-card-header">
            <span className="rules-card-icon">
              <i className="fa-solid fa-align-left" aria-hidden />
            </span>
            <div>
              <h2 className="rules-card-title">Rules &amp; Messaging</h2>
              <p className="rules-card-subtitle">What players read before, and after, the game</p>
            </div>
          </header>

          <div className="rules-card-body">
            <div className="rules-field-group">
              <label className="rules-field-label" htmlFor="rules-title-text">
                Custom Title Text
              </label>
              <textarea
                id="rules-title-text"
                className="rules-textarea"
                maxLength={MAX_TITLE_CHARS}
                value={titleText}
                onChange={(e) => setTitleText(e.target.value)}
              />
              <p className="rules-field-hint">
                <code>Max {MAX_TITLE_CHARS} characters.</code>
                <code>Shown on the main game page.</code>
              </p>
            </div>

            <div className="rules-field-group" data-tour="tour-custom-rules">
              <label className="rules-field-label">Game Rules</label>
              <div className="rules-repeater">
                {rules.map((rule, index) => {
                  const charCount = String(rule ?? "").length;
                  const overLimit = charCount > MAX_RULE_CHARS;
                  return (
                    <div className="rules-repeater-row" key={`rule-${index}`}>
                      <span className="rules-repeater-index">{index + 1}</span>
                      <div className="rules-repeater-field">
                        <textarea
                          name="rules"
                          className="rules-repeater-textarea"
                          placeholder="Rules"
                          value={rule}
                          onChange={(e) => updateRule(index, e.target.value)}
                        />
                        <p className={`rules-char-count${overLimit ? " is-over" : ""}`}>
                          {charCount}/{MAX_RULE_CHARS}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="rules-remove-btn"
                        onClick={() => removeRule(index)}
                        disabled={rules.length <= MIN_RULES || loading}
                        aria-label="Remove rule"
                        title="Remove rule"
                      >
                        <i className="fa-solid fa-xmark" aria-hidden />
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="rules-add-btn"
                onClick={addRule}
                disabled={rules.length >= MAX_RULES || loading}
              >
                <i className="fa-solid fa-plus" aria-hidden /> Add new rule
              </button>
              <p className="rules-field-hint">
                <code>Keep {MAX_RULES} rules at the most</code>
                <code>At least {MIN_RULES} rule is required</code>
                <code>Character limit of each rule is {MAX_RULE_CHARS}</code>
              </p>
            </div>

            <div className="rules-field-group">
              <label className="rules-field-label" htmlFor="rules-leaderboard-tagline">
                Leaderboard Tagline
              </label>
              <input
                id="rules-leaderboard-tagline"
                type="text"
                className="rules-input"
                maxLength={MAX_TAGLINE_CHARS}
                value={leaderboardTagline}
                onChange={(e) => setLeaderboardTagline(e.target.value)}
              />
              <p className="rules-field-hint">
                <code>Max {MAX_TAGLINE_CHARS} characters.</code>
                <code>Shown under the LEADERBOARD title.</code>
              </p>
            </div>

            {SHOW_THANKYOU_TEXT ? (
              <div className="rules-field-group">
                <label className="rules-field-label" htmlFor="rules-thankyou-text">
                  Custom Text Thank You Page
                </label>
                <textarea
                  id="rules-thankyou-text"
                  className="rules-textarea"
                  maxLength={MAX_THANKYOU_CHARS}
                  value={thankYouText}
                  onChange={(e) => setThankYouText(e.target.value)}
                />
                <p className="rules-field-hint">
                  <code>Shown on the thank you page.</code>
                  <code>Max {MAX_THANKYOU_CHARS} characters.</code>
                </p>
              </div>
            ) : null}
          </div>
        </section>

        <div className="rules-stack" data-tour="tour-page-content">
          <section className="rules-card">
            <header className="rules-card-header">
              <span className="rules-card-icon">
                <i className="fa-solid fa-sliders" aria-hidden />
              </span>
              <div>
                <h2 className="rules-card-title">Page Content</h2>
                <p className="rules-card-subtitle">Scoring and appearance</p>
              </div>
            </header>

            <div className="rules-card-body">
              <div className="rules-setting-row">
                <label htmlFor="rules-correct-points" className="rules-setting-label">
                  Total Marks Per Right Answer
                </label>
                <input
                  id="rules-correct-points"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="rules-setting-input"
                  value={correctPoints}
                  onChange={(e) =>
                    setCorrectPoints(clampCountInput(e.target.value, MAX_POINTS))
                  }
                  onBlur={() => {
                    const n = parseInt(correctPoints, 10);
                    if (correctPoints === "" || Number.isNaN(n) || n < MIN_POINTS) {
                      setCorrectPoints(String(MIN_POINTS));
                    }
                  }}
                />
              </div>

              <div className="rules-setting-row">
                <label htmlFor="rules-grid-shuffle-toggle" className="rules-setting-label">
                  Shuffle Grid
                  <span className="rules-setting-sublabel">
                    {gridShuffle
                      ? "Each player gets their own grid layout"
                      : "Every player gets the same grid layout"}
                  </span>
                </label>
                <span className="rules-switch">
                  <input
                    id="rules-grid-shuffle-toggle"
                    type="checkbox"
                    role="switch"
                    checked={gridShuffle}
                    onChange={(e) => setGridShuffle(e.target.checked)}
                  />
                  <span className="rules-switch-track" aria-hidden />
                </span>
              </div>

              <div className="rules-setting-row">
                <label htmlFor="rules-leaderboard-toggle" className="rules-setting-label">
                  Show Leaderboard
                  <span className="rules-setting-sublabel">
                    {leaderboardEnabled
                      ? "Players see the ranked leaderboard after the game"
                      : "Players only see their own score after the game"}
                  </span>
                </label>
                <span className="rules-switch">
                  <input
                    id="rules-leaderboard-toggle"
                    type="checkbox"
                    role="switch"
                    checked={leaderboardEnabled}
                    onChange={(e) => setLeaderboardEnabled(e.target.checked)}
                  />
                  <span className="rules-switch-track" aria-hidden />
                </span>
              </div>

              <div className="rules-setting-row">
                <label htmlFor="rules-hint-toggle" className="rules-setting-label">
                  Show Hint Button
                  <span className="rules-setting-sublabel">
                    Players can reveal one correct letter per hint
                  </span>
                </label>
                <span className="rules-switch">
                  <input
                    id="rules-hint-toggle"
                    type="checkbox"
                    role="switch"
                    checked={hintEnabled}
                    onChange={(e) => setHintEnabled(e.target.checked)}
                  />
                  <span className="rules-switch-track" aria-hidden />
                </span>
              </div>

              {hintEnabled ? (
                <>
                  <div className="rules-setting-row rules-setting-row--nested">
                    <label htmlFor="rules-hint-penalty-type" className="rules-setting-label">
                      Hint Penalty
                      <span className="rules-setting-sublabel">
                        {HINT_PENALTY_HELP[hintPenaltyType]}
                      </span>
                    </label>
                    <select
                      id="rules-hint-penalty-type"
                      className="rules-setting-select"
                      value={hintPenaltyType}
                      onChange={(e) => setHintPenaltyType(e.target.value)}
                    >
                      {HINT_PENALTY_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {hintPenaltyType === "points" || hintPenaltyType === "both" ? (
                    <div className="rules-setting-row rules-setting-row--nested">
                      <label htmlFor="rules-hint-penalty" className="rules-setting-label">
                        Points deducted per hint
                      </label>
                      <input
                        id="rules-hint-penalty"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        className="rules-setting-input"
                        value={hintPenalty}
                        onChange={(e) =>
                          setHintPenalty(clampCountInput(e.target.value, MAX_HINT_PENALTY))
                        }
                        onBlur={() => {
                          if (!(parseInt(hintPenalty, 10) >= 1)) {
                            setHintPenalty(String(DEFAULT_HINT_PENALTY));
                          }
                        }}
                      />
                    </div>
                  ) : null}

                  {hintPenaltyType === "time" || hintPenaltyType === "both" ? (
                    <div className="rules-setting-row rules-setting-row--nested">
                      <label htmlFor="rules-hint-time-penalty" className="rules-setting-label">
                        Penalty seconds per hint
                      </label>
                      <input
                        id="rules-hint-time-penalty"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        className="rules-setting-input"
                        value={hintTimePenalty}
                        onChange={(e) =>
                          setHintTimePenalty(normalizeCountInput(e.target.value).slice(0, 3))
                        }
                        onBlur={() => {
                          if (!(parseInt(hintTimePenalty, 10) >= 1)) {
                            setHintTimePenalty(String(DEFAULT_HINT_TIME_PENALTY));
                          }
                        }}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}

              <div className="rules-color-grid">
                <label className="rules-color-chip">
                  <span className="rules-color-chip-swatch" style={{ background: textColor }}>
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Text Color</span>
                </label>

                <label className="rules-color-chip">
                  <span className="rules-color-chip-swatch" style={{ background: buttonColor }}>
                    <input
                      type="color"
                      value={buttonColor}
                      onChange={(e) => setButtonColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Button Color</span>
                </label>

                <label className="rules-color-chip">
                  <span
                    className="rules-color-chip-swatch"
                    style={{ background: buttonTextColor }}
                  >
                    <input
                      type="color"
                      value={buttonTextColor}
                      onChange={(e) => setButtonTextColor(e.target.value)}
                    />
                  </span>
                  <span className="rules-color-chip-label">Button Text Color</span>
                </label>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="rules-sticky-actions">
        <button
          type="button"
          className="save-and-continue"
          data-tour="tour-actions"
          onClick={() => handleSave({ goNext: true })}
          disabled={loading}
        >
          {loading ? "Saving..." : "Next"} <i className="fa-solid fa-angle-double-right" aria-hidden />
        </button>
      </div>

      <RulesFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default Rules;
